import {ErrorHandler, Injectable, inject} from '@angular/core';
import {LoggerService} from './logger.service';

const CHUNK_FAILED = /Loading chunk \S+ failed/;
const IDB_LOST = /Indexed Database server lost/;
const RELOAD_AT_KEY = 'am_stale_bundle_reload_at';

// Non-Error throwables stringify uselessly in the logs (a thrown function
// arrives as its minified source, e.g. "()=>G"), so label them with type
// and constructor to make the next occurrence traceable.
function describeError(err: any): string {
  if (err?.message != null) return err.message;
  const ctor = typeof err === 'object' && err?.constructor?.name && err.constructor.name !== 'Object'
    ? ` ${err.constructor.name}` : '';
  return `non-Error thrown (${typeof err}${ctor}): ${String(err)}`;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private logger = inject(LoggerService);

  handleError(error: any): void {
    const err = error?.rejection ?? error;
    const message = describeError(err);

    // Stale bundle after a deploy or live update: chunks the running app
    // references no longer exist. A reload picks up a consistent set.
    if (err?.name === 'ChunkLoadError' || CHUNK_FAILED.test(message)) {
      this.logger.logError(`Stale bundle, reloading: ${message}`, {stack: err?.stack});
      if (this.markReload()) {
        this.reloadPage();
        return;
      }
    }

    // iOS WebKit drops the IndexedDB connection when the webview is
    // suspended; storage stays broken until the page is refreshed, which
    // is exactly what the WebKit error message asks for.
    if (IDB_LOST.test(message)) {
      this.logger.logError(`IndexedDB connection lost, reloading: ${message}`, {stack: err?.stack});
      if (this.markReload()) {
        this.reloadPage();
        return;
      }
    }

    this.logger.logError(`Uncaught error: ${message}`, {stack: err?.stack});
    // keep default devtools visibility; LoggerService swallows its own failures,
    // so this handler cannot recurse
    console.error(error);
  }

  // at most one auto-reload per minute per tab, so a still-broken
  // bundle cannot put the app into a reload loop
  private markReload(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);
      if (Date.now() - last < 60_000) {
        return false;
      }
      sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
      return true;
    } catch {
      return false;
    }
  }

  private reloadPage(): void {
    window.location.reload();
  }
}
